import { useQuery, useMutation } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { first, get, pick, sum } from "lodash";
import type { PayoutCreateInput } from "~/layers/admin/models/interfaces/Payout";
import { formatPrice } from "~/layers/admin/utils/formats";

type Balance = {
  amount: number;
  currency: string;
};

type AccountBalance = {
  /** Funds that aren’t available in the balance yet. */
  pending?: Balance[];

  /**Available funds that you can transfer or pay out automatically */
  available?: Balance[];

  /** Funds held due to negative balances on connected accounts */
  connect_reserved?: Balance[];

  /** Funds from store charges that are pending transfer to Stripe balance. These funds are not yet reflected in the available or pending balances above. */
  pending_transfers?: Balance[];

  /** Funds that you can pay out using Instant Payouts. */
  instant_available?: Balance[];
};

type BalanceQueryResult = {
  wsBalance: AccountBalance;
};

type PayoutQueryResult = {
  wsCreatePayout: {
    ok: boolean;
    balance?: AccountBalance;
  };
};

const QUERIES = {
  BalanceQuery: `
    pending {
      amount
      currency
    }
    available {
      amount
      currency
    }
    connect_reserved {
      amount
      currency
    }
    pending_transfers {
      amount
      currency
    }
    instant_available {
      amount
      currency
    }
  `,
  get balance() {
    return gql`
      query {
        wsBalance {
          ${this.BalanceQuery}
        }
      }
    `;
  },
  get payout() {
    return gql`
      mutation ($data: PayoutCreateInput!) {
        wsCreatePayout(data: $data) {
          ok
          balance {
            ${this.BalanceQuery}
          }
        }
      }
    `;
  },
};

// TODO: this util assumes all balances are the same currency
export const useBalances = () => {
  const balance = shallowRef<AccountBalance>();
  const pendingRaw = ref(0);
  const availableRaw = ref(0);
  const reservedRaw = ref(0);
  const instantAvailableRaw = ref(0);
  const pendingTransfersRaw = ref(0);
  const currency = ref<string>();

  const { currency: wsCurrency } = useWorkspace();
  const balanceQuery = useQuery<BalanceQueryResult>(QUERIES.balance);
  const payoutMutation = useMutation<PayoutQueryResult>(QUERIES.payout);
  const loading = computed(() => {
    return balanceQuery.loading.value || payoutMutation.loading.value;
  });

  const sumAmount = (target: keyof AccountBalance) => {
    const balances = get(balance.value, target) || [];
    return sum(balances.map((v) => v.amount)) / 100;
  };

  const format = (v: number) => {
    return formatPrice(v, currency.value);
  };

  balanceQuery.onResult((result) => {
    balance.value = result.data?.wsBalance;
    pendingRaw.value = sumAmount("pending");
    availableRaw.value = sumAmount("available");
    reservedRaw.value = sumAmount("connect_reserved");
    instantAvailableRaw.value = sumAmount("instant_available");
    pendingTransfersRaw.value = sumAmount("pending_transfers");

    currency.value = execFn(() => {
      const balances = pick(balance.value || {}, [
        "available",
        "pending",
        "connect_reserved",
        "instant_available",
        "pending_transfers",
      ]);

      const currencies = Object.values(balances)
        .filter((b) => !!b)
        .map((b) => b.map((x) => x.currency))
        .flat();

      if (unique(currencies).length > 1) {
        throw new Error("Different balance currencies found");
      }

      const code = first(currencies);
      if (code) {
        return code.toUpperCase();
      }
    });
  });

  const currencyCode = computed(() => {
    return currency.value || wsCurrency.value?.code;
  });

  const pending = computed(() => format(pendingRaw.value));
  const available = computed(() => format(availableRaw.value));
  const instantAvailable = computed(() => format(instantAvailableRaw.value));
  const reserved = computed(() => format(reservedRaw.value));
  const pendingTransfers = computed(() => format(pendingTransfersRaw.value));

  const payout = async (data: PayoutCreateInput) => {
    const result = await payoutMutation.mutate({ data });

    const ok = !!result?.data?.wsCreatePayout?.ok;

    if (ok && result?.data?.wsCreatePayout?.balance) {
      balance.value = result.data.wsCreatePayout.balance;
    }

    return ok;
  };

  return {
    loading,
    pendingRaw,
    availableRaw,
    reservedRaw,
    instantAvailableRaw,
    pendingTransfersRaw,
    currency: currencyCode,
    pending,
    available,
    instantAvailable,
    reserved,
    pendingTransfers,
    payout,
  };
};
